import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  ScaleIcon,
  ChartBarIcon,
  EnvelopeIcon,
  BuildingLibraryIcon,
  MapPinIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */
const coreValueProps = [
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Builder & Validation',
    description:
      'End-to-end HMDA data preparation — from raw LOS export to submission-ready LAR file. 160+ edit checks with plain-language explanations. ML-assisted column matching for Encompass, Jack Henry, LaserPro, Calyx, and MeridianLink. Data quality scoring, geocoding with FFIEC Census API integration, and examiner-exportable validation logs. Covers Data Quality, Syntactical, and Validity edit checks per CFPB LAR formatting guidance.',
  },
  {
    icon: BuildingLibraryIcon,
    title: 'CRA Module',
    description:
      'Community Reinvestment Act analysis purpose-built for community banks. Assessment area mapping, loan-to-deposit ratio tracking, qualified investment identification, and performance context documentation. CRA exam prep workflows with examiner-ready reporting. Tracks lending, investment, and service test components, with census tract-level analysis against FFIEC demographic data.',
  },
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description:
      'Denial rate and pricing disparity analysis with full statistical testing (logistic regression, OLS, Fisher\'s exact). Redlining risk analysis with census tract mapping and demographic overlays using FFIEC and Census Bureau data. Marketing content screening across four risk dimensions: geographic targeting, prohibited basis language, steering indicators, and discouragement patterns. Paired bias testing across 8 protected-class dimensions with significance thresholds per NIST AI 100-1.',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression Analysis Engine',
    description:
      'Logistic regression for denial and steering disparity models. OLS regression for pricing analysis. Matched-pair analysis for examiner file review preparation. Regulator-aware output formatted for FDIC, OCC, Fed, and NCUA standards. Auto-triggers on HMDA upload with quarterly scheduling. Outputs include odds ratios, p-values, confidence intervals, and examiner narrative summaries.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Regulatory Change Intelligence',
    description:
      'Automated monitoring of regulatory updates from 8 federal agencies — CFPB, FDIC, OCC, NCUA, FRB, FHFA, SEC, and FinCEN. Daily RSS scanning with keyword classification, compliance area tagging, and priority assignment. Immediate alerts when new guidance affects your compliance program or your clients\' programs.',
  },
  {
    icon: MapPinIcon,
    title: 'Marketing Compliance Monitoring',
    description:
      'Every ad, email, social post, and disclosure screened against Reg Z, UDAAP, TILA, TISA, FTC Act, and fair lending requirements. AI-powered detection with a deterministic rules engine verification layer. Mandatory human approval workflows — no autonomous compliance decisions. Examiner-ready audit trail with SHA-256 hash-chained logging on every check.',
  },
];

const collaborationModels = [
  {
    num: '01',
    title: 'Advisory Practice Enhancement',
    description:
      'DD&F brings compliance strategy expertise. Clarid provides the continuous execution layer — HMDA validation, fair lending monitoring, CRA tracking — that runs between engagements. Banks get the advisory guidance and the technology that maintains the standards DD&F helped establish.',
  },
  {
    num: '02',
    title: 'Client Referral & Co-Delivery',
    description:
      'DD&F refers community bank clients who need automated compliance infrastructure. Clarid delivers the technology relationship. DD&F maintains the advisory relationship. Both firms deliver more value to the bank — and DD&F is positioned as the advisor who connected them with best-in-class compliance technology.',
  },
  {
    num: '03',
    title: 'Exam Preparation Partnership',
    description:
      'Combine DD&F\'s consulting experience with Clarid\'s automated pre-examination documentation. HMDA LAR validation reports, fair lending statistical outputs, CRA performance documentation, and marketing compliance audit logs — all examiner-ready before the first information request arrives.',
  },
];

const dueDiligenceDocs = [
  'AI Governance Policy',
  'FS AI RMF Gap Analysis (230 control objectives, 100% coverage)',
  'Model Inventory with validation framework',
  'Bias Testing Methodology & Results',
  'Data Flow Architecture Documentation',
  'Information Security Program (WISP)',
  'Incident Response Plan',
  'Business Continuity Plan',
  'Vendor Inventory (third-party dependencies)',
];

const nextSteps = [
  { num: 1, title: 'This Conversation', description: 'Explore alignment between DD&F\'s compliance advisory practice and Clarid\'s HMDA, CRA, and fair lending platform' },
  { num: 2, title: 'Platform Walkthrough', description: 'Live demonstration of the HMDA LAR builder, fair lending analysis engine, and CRA module using realistic community bank data' },
  { num: 3, title: 'Joint Client Pilot', description: 'Identify 2–3 DD&F banking clients for a structured pilot with defined success criteria across HMDA, CRA, or fair lending workflows' },
  { num: 4, title: 'Partnership Formalization', description: 'Define referral structure, co-delivery model, and go-to-market approach that fits DD&F\'s existing client engagement model' },
];

/* ─── Component ─── */
export default function DDFPartnerPage() {
  useEffect(() => {
    document.title = 'DD&F + Clarid — AI-Native Compliance for Community Banks';

    const metaTags: Record<string, string> = {
      'og:title': 'DD&F + Clarid — Strategic Partnership',
      'og:description': 'AI-native compliance platform built for HMDA, CRA, and fair lending. Built by a community bank CMO, advised by former FDIC and Federal Reserve examiners.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'DD&F + Clarid Partnership',
      'twitter:description': 'The compliance platform examiners helped build.',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <span className="text-2xl font-display font-bold text-navy-900 tracking-tight">DD&amp;F</span>
            <span className="text-slate-300 text-xl font-light">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
          </div>
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-600 mb-8">
              Strategic Partnership
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              HMDA. CRA. Fair Lending. Automated.
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid is an AI-native compliance operating system for community banks and credit unions —
              built by a community bank CMO, advised by a former FDIC Supervisory Examiner and a former
              Federal Reserve fair lending subject matter expert, and designed to automate the compliance
              workflows banks, credit unions, and their advisors manage every day.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: The Problem ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                The Compliance Gap Community Banks and Credit Unions Can't Close Alone
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Community banks and credit unions face the same regulatory expectations as institutions
                  ten times their size — with a fraction of the compliance staff. HMDA submissions require
                  meticulous data quality validation across hundreds of fields. CRA performance requires
                  continuous documentation, not quarterly check-ins. Fair lending monitoring requires statistical
                  analysis that most compliance officers don't have time to run manually.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Examiners expect continuous processes and examiner-ready documentation. For institutions
                  managing HMDA, CRA, and fair lending simultaneously with 1–3 compliance staff, the math
                  doesn't work without technology.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Advisory firms like DD&F provide the strategic framework. Clarid provides the automated
                  execution layer that makes that framework operational between engagements — and produces
                  the documentation that makes exam preparation manageable.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  That's the collaboration we're proposing.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Core Value Props ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
              Where Clarid Delivers
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {coreValueProps.map((prop, i) => (
              <ScaleIn key={prop.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 group-hover:scale-110 transition-all duration-300">
                      <prop.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{prop.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{prop.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.4}>
            <p className="mt-14 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid integrates into the tools bankers already use — Microsoft Copilot, Claude, ChatGPT — through
              MCP (Model Context Protocol). Compliance intelligence embedded in daily workflows, not
              locked in a separate portal.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 4: Built with Examination Perspective ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-300 mb-5">
                Built With the Examination Perspective
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                Designed by People Who Sit Across from Examiners
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                Clarid was built by a community bank CMO who manages marketing compliance, HMDA
                reporting, CRA documentation, and examiner relationships as part of his daily work —
                and advised by a former FDIC Supervisory Examiner and a former Federal Reserve fair
                lending subject matter expert with decades of combined examination experience across
                hundreds of community bank examinations.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 mt-6">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500 h-full">
                <UsersIcon className="h-7 w-7 text-teal-400 mb-5" />
                <h3 className="text-2xl font-display font-bold text-white mb-4">Built by the Buyer</h3>
                <p className="text-lg text-slate-400 leading-relaxed">
                  Not engineers guessing at compliance needs. A community bank CMO who lives these workflows
                  daily and uses Clarid's own outputs in his institution's compliance program.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500 h-full">
                <BuildingLibraryIcon className="h-7 w-7 text-teal-400 mb-5" />
                <h3 className="text-2xl font-display font-bold text-white mb-4">Examiner-Advised</h3>
                <p className="text-lg text-slate-400 leading-relaxed">
                  Every rule engine, workflow, and output is validated against what FDIC and Federal Reserve
                  examiners actually evaluate — not against what compliance teams assume they evaluate.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.3}>
              <div className="p-10 lg:p-12 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500 h-full">
                <DocumentCheckIcon className="h-7 w-7 text-teal-400 mb-5" />
                <h3 className="text-2xl font-display font-bold text-white mb-4">FS AI RMF Coverage</h3>
                <p className="text-lg text-slate-400 leading-relaxed">
                  100% coverage of the U.S. Treasury's Financial Services AI Risk Management Framework —
                  230 control objectives across GOVERN, MAP, MEASURE, and MANAGE. Validated within 14 days
                  of the framework's release.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 5: Collaboration Models ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                How This Works Together
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
                DD&F's compliance advisory expertise combined with Clarid's AI-native automation gives
                community banks and credit unions both strategic guidance and continuous execution — the
                advisory framework and the technology layer working together.
              </p>
            </FadeUp>

            <div className="space-y-6 lg:space-y-8">
              {collaborationModels.map((model, i) => (
                <ScaleIn key={model.num} delay={i * 0.1}>
                  <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500">
                    <div className="flex items-start gap-6">
                      <span className="text-3xl font-display font-bold text-navy-900/20 flex-shrink-0 mt-0.5">
                        {model.num}
                      </span>
                      <div>
                        <h3 className="text-2xl font-display font-bold text-navy-900 mb-3">{model.title}</h3>
                        <p className="text-lg text-slate-500 leading-relaxed">{model.description}</p>
                      </div>
                    </div>
                  </div>
                </ScaleIn>
              ))}
            </div>

            <FadeUp delay={0.4}>
              <p className="mt-12 text-center text-lg text-slate-500 italic">
                We'd welcome a conversation about which model — or combination — fits best within
                DD&F's existing client engagement structure.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 6: Due Diligence ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                We've Done the Homework
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-14 leading-relaxed">
                Clarid maintains a comprehensive vendor due diligence package designed to meet
                interagency guidance on third-party relationships. Available documentation includes:
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-5">
                  {dueDiligenceDocs.map((doc, i) => (
                    <FadeIn key={doc} delay={0.15 + i * 0.05}>
                      <li className="flex items-start gap-4">
                        <CheckCircleIcon className="h-6 w-6 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-600">{doc}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.6}>
              <p className="mt-12 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
                Our Executive Due Diligence Summary, Data Flow Architecture, and full FS AI RMF gap
                analysis are available for review. SOC 2 Type II is on our 12-month roadmap — we're
                currently documenting controls and scoping the audit.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 7: Moving Forward ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
                Moving Forward
              </h2>
            </FadeUp>

            <div className="space-y-10 lg:space-y-12">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-navy-900 text-white flex items-center justify-center text-lg font-bold">
                      {step.num}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 8: Contact ─── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder & CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-teal-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-teal-700 transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-base font-display font-bold text-navy-900/30">DD&amp;F</span>
            <span className="text-slate-200">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
          </div>
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
